import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Trash2, Bot, Briefcase, Calculator, Loader2 } from 'lucide-react';
import { ChatMessage, AIChatMode } from '../../../types/aiChat';
import { ChatMessageBubble } from '../../molecules/ChatMessageBubble/ChatMessageBubble';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ButtonAction from '../../atoms/buttonAction/view';
import './AIChatDrawer.css';

interface AIChatDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    messages: ChatMessage[];
    isLoading: boolean;
    mode: AIChatMode;
    currentView?: string;
    userName?: string;
    onSendMessage: (text: string) => void;
    onClearChat: () => void;
    error?: string | null;
}

export const AIChatDrawer: React.FC<AIChatDrawerProps> = ({
    isOpen,
    onClose,
    messages,
    isLoading,
    mode,
    currentView,
    userName,
    onSendMessage,
    onClearChat,
    error,
}) => {
    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [isOpen, messages, isLoading]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 180)}px`;
        }
    }, [inputText]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputText.trim() || isLoading) return;
        onSendMessage(inputText);
        setInputText('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="ai-drawer-overlay" onClick={onClose}>

            <div className="ai-drawer-content" onClick={(e) => e.stopPropagation()}>
                {/* Header discreto estilo Gemini */}
                <div className="ai-drawer-header">
                    <div className="ai-drawer-header__left">
                        <Sparkles size={18} className="ai-sparkles-icon" />
                        <span className="ai-drawer-title">Asistente IA</span>
                        <span className="ai-mode-tag">
                            {mode === 'business' ? 'Negocio' : 'Contador'}
                        </span>
                    </div>
                    <div className="ai-drawer-header__actions">
                        {messages.length > 0 && (
                            <button
                                className="ai-drawer-action-btn"
                                onClick={onClearChat}
                                title="Limpiar conversación"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                        <button
                            className="ai-drawer-action-btn close"
                            onClick={onClose}
                            title="Cerrar chat"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Messages Body */}
                <div className="ai-drawer-body">
                    {messages.length === 0 ? (
                        <div className="ai-drawer-hero">
                            <h2 className="ai-hero-title">
                                Hola, <span className="ai-hero-name">{userName || ''}</span>
                            </h2>
                            <h3 className="ai-hero-subtitle">¿En qué puedo ayudarte?</h3>

                            <div className="ai-drawer-suggestions">
                                {mode === 'business' ? (
                                    <>
                                        <button onClick={() => onSendMessage('¿Cómo han estado las ventas esta semana?')}>
                                            <Sparkles size={14} />
                                            <span>Analizar ventas de esta semana</span>
                                        </button>
                                        <button onClick={() => onSendMessage('¿Cuáles son los productos más vendidos?')}>
                                            <Briefcase size={14} />
                                            <span>Ver productos más vendidos</span>
                                        </button>
                                        <button onClick={() => onSendMessage('Resumen de ingresos y flujo de caja')}>
                                            <Calculator size={14} />
                                            <span>Resumen de flujo de caja</span>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button onClick={() => onSendMessage('¿Hay descuadres en los asientos contables de hoy?')}>
                                            <Sparkles size={14} />
                                            <span>Auditar descuadres de hoy</span>
                                        </button>
                                        <button onClick={() => onSendMessage('¿Cuál es el saldo actual de la cuenta 1105?')}>
                                            <Calculator size={14} />
                                            <span>Consultar saldo cuenta 1105</span>
                                        </button>
                                        <button onClick={() => onSendMessage('Revisar impuestos y comprobantes')}>
                                            <Briefcase size={14} />
                                            <span>Revisar impuestos y comprobantes</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, idx) => (
                                <ChatMessageBubble key={idx} message={msg} onCloseChat={onClose} />
                            ))}
                            {isLoading && (
                                <div className="chat-bubble-container assistant">
                                    <div className="chat-bubble-avatar">
                                        <Bot size={16} />
                                    </div>
                                    <div className="chat-bubble-content">
                                        <div className="chat-bubble-text loading">
                                            <Loader2 size={16} className="ai-loading-spin" />
                                            <span>Pensando...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {error && (
                                <div className="ai-drawer-error-banner">
                                    ❌ {error}
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Footer Input Gemini / Antigravity Box 1:1 */}
                <div className="ai-drawer-footer-wrapper">
                    <p className='ai-error-banner'>EL asistente usa IA y puede cometer errores</p>
                    {/* Tarjeta contenedora exterior de contexto */}
                    <div className="ai-context-outer-card">
                        <div className="ai-context-outer-header">
                            <div className="ai-context-outer-title">
                                <span className="ai-context-hash">#</span>
                                <span>Contexto: {currentView || (mode === 'business' ? 'Métricas de Negocio' : 'Auditoría Contable')}</span>
                            </div>
                        </div>

                        {/* Cuadro de texto interior redondeado */}

                        <form onSubmit={handleSubmit} className="ai-inner-input-box">
                            <textarea
                                ref={inputRef}
                                rows={1}
                                placeholder="Escribe una pregunta o consulta..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={isLoading}
                            />

                            <div className="ai-inner-bottom-bar">
                                <ButtonAction
                                    onClick={() => {
                                        if (!inputText.trim() || isLoading) return;
                                        onSendMessage(inputText);
                                        setInputText('');
                                    }}
                                    disabled={!inputText.trim() || isLoading}
                                    btnKey="ai-send-msg"
                                    style={{ width: '30px', height: '30px' }}
                                >
                                    <Send size={14} />
                                </ButtonAction>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
